import { useState, useRef, useCallback, useEffect } from 'react';

interface Props {
  beforeImg: string;
  afterImg: string;
  beforeLabel?: string;
  afterLabel?: string;
}

export default function BeforeAfterSlider({
  beforeImg,
  afterImg,
  beforeLabel = 'BEFORE',
  afterLabel = 'AFTER',
}: Props) {
  const [sliderPos, setSliderPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const calcPos = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setSliderPos((x / rect.width) * 100);
  }, []);

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    calcPos(e.clientX);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    calcPos(e.touches[0].clientX);
  };

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      calcPos(e.clientX);
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;
      calcPos(e.touches[0].clientX);
    };
    const onUp = () => setIsDragging(false);

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [isDragging, calcPos]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden select-none"
      style={{ cursor: isDragging ? 'ew-resize' : 'col-resize' }}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
    >
      {/* AFTER (base layer - right side) */}
      <img
        src={afterImg}
        alt="After"
        className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none"
        draggable={false}
      />

      {/* BEFORE (clipped layer - left side) */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ width: `${sliderPos}%` }}
      >
        <img
          src={beforeImg}
          alt="Before"
          className="absolute inset-0 w-full h-full object-cover object-top"
          style={{ width: `${10000 / sliderPos}%`, maxWidth: 'none' }}
          draggable={false}
        />
      </div>

      {/* Divider Line */}
      <div
        className="absolute top-0 bottom-0 w-0.5 pointer-events-none"
        style={{ left: `${sliderPos}%`, backgroundColor: '#c9a84c', transform: 'translateX(-50%)' }}
      />

      {/* Handle */}
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 pointer-events-none"
        style={{ left: `${sliderPos}%` }}
      >
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{
            backgroundColor: '#c9a84c',
            boxShadow: '0 0 0 3px rgba(201,168,76,0.3)',
          }}
        >
          <div className="flex items-center gap-0.5">
            <i className="ri-arrow-left-s-line text-[#0d0d0d] text-sm leading-none" />
            <i className="ri-arrow-right-s-line text-[#0d0d0d] text-sm leading-none" />
          </div>
        </div>
      </div>

      {/* Labels */}
      <div
        className="absolute top-3 left-3 text-xs font-bold px-2.5 py-1 rounded-full pointer-events-none"
        style={{ backgroundColor: '#0d0d0dcc', color: '#ffffff99', border: '1px solid #ffffff20' }}
      >
        {beforeLabel}
      </div>
      <div
        className="absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-full pointer-events-none"
        style={{ backgroundColor: '#c9a84ccc', color: '#0d0d0d' }}
      >
        {afterLabel}
      </div>
    </div>
  );
}
